import { useTranslation } from "react-i18next";
import {
  addDays,
  avatarColorForId,
  fmtNo,
  formatAppointmentTimeDetail,
  initials,
  slotToTime,
} from "./schedule-utils";

function Dot({ color }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: 999,
        background: color,
        marginRight: 2,
      }}
    />
  );
}

function SelectedTime({ appointment, weekStart, onClear, fixedTime, t }) {
  const d = addDays(weekStart, appointment.day);
  const start = slotToTime(appointment.startSlot);
  const end = slotToTime(appointment.endSlot);
  const mins = (appointment.endSlot - appointment.startSlot) * 15;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const durLabel =
    hrs > 0 ? `${hrs}t${rem ? ` ${rem}m` : ""}` : `${rem}m`;

  return (
    <div className="ra-timechip">
      <div className="tc-date">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M2 6h12M5.5 2v2M10.5 2v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        {fmtNo(d)}
      </div>
      <div className="tc-range">
        {fixedTime ? (
          <>
            <span>{t("Fixed time")}:</span> {start}
          </>
        ) : (
          <>
            {start} – {end}
            <span className="tc-dur">{durLabel}</span>
          </>
        )}
        <button
          type="button"
          onClick={onClear}
          style={{
            marginLeft: "auto",
            background: "transparent",
            border: 0,
            color: "var(--text-faint)",
            cursor: "pointer",
            fontSize: 12,
            padding: "2px 6px",
            borderRadius: 6,
          }}
        >
          {t("report.agreement.redesign.clear_time")}
        </button>
      </div>
      <div className="tc-hint" style={{ marginTop: 8, fontSize: 12, color: "var(--text-muted)" }}>
        {formatAppointmentTimeDetail(appointment, fixedTime)}
      </div>
    </div>
  );
}

export default function ReportAgreementFormColumn({
  employees,
  employeeId,
  setEmployeeId,
  search,
  setSearch,
  availabilityFor,
  showAvailability,
  appointment,
  setAppointment,
  weekStart,
  fixedTime,
  setFixedTime,
  requireConfirmation,
  setRequireConfirmation,
  canPickEmployee,
  /** Which sections to render: "all" or subset for mobile wizard steps. */
  sections = "all",
  scheduleHelpKey,
  dragHintKey,
  className = "",
}) {
  const { t } = useTranslation();
  const show = (key) =>
    sections === "all" || (Array.isArray(sections) && sections.includes(key));

  return (
    <div className={`ra-form-col${className ? ` ${className}` : ""}`}>
      {show("employee") && (
        <div>
          <div className="ra-section-label">
            <Dot color="#1f8ce8" /> {t("report.agreement.redesign.executing_section")}
          </div>
          <div className="ra-section-help">
            {t("report.agreement.redesign.executing_help")}
          </div>

          {canPickEmployee ? (
            <>
              <div className="ra-emp-search">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  placeholder={t("report.agreement.redesign.search_employee")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="ra-emp-grid">
                {employees.map((e) => {
                  const avail = availabilityFor(e.id);
                  const color = avatarColorForId(e.id);
                  return (
                    <button
                      key={e.id}
                      className={`ra-emp-card${employeeId === e.id ? " selected" : ""}`}
                      onClick={() => setEmployeeId(e.id)}
                      type="button"
                    >
                      <div className="ra-emp-avatar" style={{ background: color }}>
                        {initials(e.displayTitle || e.name)}
                      </div>
                      <div className="ra-emp-meta">
                        <div className="ra-emp-name">{e.displayTitle || e.name}</div>
                        <div className="ra-emp-role">{e.roleLabel || e.email || ""}</div>
                      </div>
                      {showAvailability && (
                        <div className={`ra-emp-availability ra-avail-${avail}`}>
                          <span className="ra-avail-dot" />
                          {avail === "free" && t("report.agreement.redesign.avail_free")}
                          {avail === "part" && t("report.agreement.redesign.avail_partial")}
                          {avail === "busy" && t("report.agreement.redesign.avail_busy")}
                          {avail === "absence" && t("report.agreement.redesign.avail_absence")}
                        </div>
                      )}
                    </button>
                  );
                })}
                {employees.length === 0 && (
                  <div
                    style={{
                      color: "var(--text-muted)",
                      fontSize: 13,
                      padding: 8,
                    }}
                  >
                    {t("report.agreement.redesign.no_employees")}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="ra-timechip">
              <div className="tc-date">
                {employees[0]?.displayTitle || employees[0]?.name || t("report.agreement.executing_you")}
              </div>
              <div className="tc-range" style={{ marginTop: 4 }}>
                {t("report.agreement.redesign.executing_locked_hint")}
              </div>
            </div>
          )}
        </div>
      )}

      {show("schedule") && (
        <div>
          <div className="ra-section-label">
            <Dot color="#16a34a" /> {t("report.agreement.redesign.schedule_section")}
          </div>
          <div className="ra-section-help">
            {t(scheduleHelpKey || "report.agreement.redesign.schedule_help")}
          </div>

          {appointment ? (
            <SelectedTime
              appointment={appointment}
              weekStart={weekStart}
              onClear={() => setAppointment(null)}
              fixedTime={fixedTime}
              t={t}
            />
          ) : (
            <div className="ra-timechip empty">
              <div className="tc-hint">
                {t(dragHintKey || "report.agreement.redesign.drag_hint")}{" "}
                <b>{t("report.agreement.redesign.drag_hint_bold")}</b>{" "}
                {t("report.agreement.redesign.drag_hint_suffix")}
                <div style={{ marginTop: 6, color: "var(--text-faint)" }}>
                  {t("report.agreement.redesign.drag_tip")}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {show("settings") && (
        <div>
          <div className="ra-section-label">
            <Dot color="#94a3b8" /> {t("report.agreement.redesign.settings_section")}
          </div>
          <div className="ra-toggle-row">
            <div className="grow">
              <div className="lbl">{t("Fixed time")}</div>
              <div className="sub">
                {fixedTime
                  ? t("report.agreement.redesign.fixed_time_on_hint")
                  : t("report.agreement.redesign.fixed_time_off_hint")}
              </div>
            </div>
            <button
              type="button"
              className={`ra-tog${fixedTime ? " on" : ""}`}
              onClick={() => setFixedTime(!fixedTime)}
              aria-pressed={fixedTime}
              aria-label={t("Fixed time")}
            />
          </div>
          <div className="ra-toggle-row">
            <div className="grow">
              <div className="lbl">{t("Appointment confirmation required")}</div>
              <div className="sub">
                {requireConfirmation
                  ? t("report.agreement.confirm_hint_required")
                  : t("report.agreement.confirm_hint_optional")}
              </div>
            </div>
            <button
              type="button"
              className={`ra-tog${requireConfirmation ? " on" : ""}`}
              onClick={() => setRequireConfirmation(!requireConfirmation)}
              aria-pressed={requireConfirmation}
              aria-label={t("Appointment confirmation required")}
            />
          </div>
        </div>
      )}
    </div>
  );
}
